import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useData } from '../../contexts/DataContext';
import { ChevronRightIcon, FunnelIcon, CheckCircleIcon, ClockIcon, ExclamationTriangleIcon } from '@heroicons/react/24/outline';

const AdminSupportDashboard: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { tickets, isLoading } = useData();
  
  const [statusFilter, setStatusFilter] = useState<'all' | 'open' | 'in_progress' | 'resolved'>('all');
  const [categoryFilter, setCategoryFilter] = useState<'all' | 'general' | 'technical' | 'payment' | 'release' | 'account'>('all');
  const [priorityFilter, setPriorityFilter] = useState<'all' | 'low' | 'normal' | 'high' | 'urgent'>('all');
  const [searchQuery, setSearchQuery] = useState('');

  // Only show if user is admin
  if (!user || user.role !== 'admin') {
    return (
      <div className="max-w-6xl mx-auto p-6">
        <div className="text-center py-12">
          <h3 className="text-lg font-medium text-white mb-2">
            Erişim Reddedildi
          </h3>
          <p className="text-gray-300">
            Bu sayfaya erişmek için admin yetkisine sahip olmalısınız.
          </p>
        </div>
      </div>
    );
  }

  const filteredTickets = tickets.filter(ticket => {
    // Status filter
    if (statusFilter !== 'all' && ticket.status !== statusFilter) return false;
    
    // Category filter
    if (categoryFilter !== 'all' && ticket.category !== categoryFilter) return false;
    
    // Priority filter
    if (priorityFilter !== 'all' && ticket.priority !== priorityFilter) return false;
    
    // Search filter
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      return (
        ticket.subject.toLowerCase().includes(query) ||
        ticket.artist?.display_name?.toLowerCase().includes(query) ||
        ticket.artist?.email?.toLowerCase().includes(query)
      );
    }
    
    return true;
  });

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'open':
        return <ExclamationTriangleIcon className="w-5 h-5 text-red-400" />;
      case 'in_progress':
        return <ClockIcon className="w-5 h-5 text-amber-400" />;
      case 'resolved':
        return <CheckCircleIcon className="w-5 h-5 text-green-400" />;
      default:
        return null;
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'open':
        return 'bg-red-500/20 text-red-300 border-red-500/30';
      case 'in_progress':
        return 'bg-amber-500/20 text-amber-300 border-amber-500/30';
      case 'resolved':
        return 'bg-green-500/20 text-green-300 border-green-500/30';
      default:
        return 'bg-gray-500/20 text-gray-300 border-gray-500/30';
    }
  };

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case 'urgent':
        return 'bg-red-500/20 text-red-300 border-red-500/30';
      case 'high':
        return 'bg-orange-500/20 text-orange-300 border-orange-500/30';
      case 'normal':
        return 'bg-blue-500/20 text-blue-300 border-blue-500/30';
      case 'low':
        return 'bg-gray-500/20 text-gray-300 border-gray-500/30';
      default:
        return 'bg-gray-500/20 text-gray-300 border-gray-500/30';
    }
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffTime = Math.abs(now.getTime() - date.getTime());
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    
    if (diffDays === 1) {
      return t('common.days', { count: 1 });
    } else if (diffDays < 7) {
      return t('common.days', { count: diffDays });
    } else {
      return date.toLocaleDateString();
    }
  };

  const getTicketStats = () => {
    const totalTickets = tickets.length;
    const openTickets = tickets.filter(t => t.status === 'open').length;
    const inProgressTickets = tickets.filter(t => t.status === 'in_progress').length;
    const resolvedTickets = tickets.filter(t => t.status === 'resolved').length;
    const urgentTickets = tickets.filter(t => t.priority === 'urgent').length;
    const highPriorityTickets = tickets.filter(t => t.priority === 'high').length;

    return {
      totalTickets,
      openTickets,
      inProgressTickets,
      resolvedTickets,
      urgentTickets,
      highPriorityTickets
    };
  };

  const stats = getTicketStats();

  if (isLoading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-400"></div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto p-6">
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-white mb-2">
          {t('support.admin.title')}
        </h1>
        <p className="text-gray-300">
          {t('support.admin.subtitle')}
        </p>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-6 gap-4 mb-8">
        <div className="bg-gray-800/30 backdrop-blur-sm rounded-lg border border-gray-700/50 p-4">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <div className="w-8 h-8 bg-blue-500/20 rounded-full flex items-center justify-center">
                <span className="text-blue-300 font-medium text-sm">{stats.totalTickets}</span>
              </div>
            </div>
            <div className="ml-3">
              <p className="text-sm font-medium text-gray-300">{t('support.admin.totalTickets')}</p>
            </div>
          </div>
        </div>

        <div className="bg-gray-800/30 backdrop-blur-sm rounded-lg border border-gray-700/50 p-4">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <div className="w-8 h-8 bg-red-500/20 rounded-full flex items-center justify-center">
                <span className="text-red-300 font-medium text-sm">{stats.openTickets}</span>
              </div>
            </div>
            <div className="ml-3">
              <p className="text-sm font-medium text-gray-300">{t('support.admin.openTickets')}</p>
            </div>
          </div>
        </div>

        <div className="bg-gray-800/30 backdrop-blur-sm rounded-lg border border-gray-700/50 p-4">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <div className="w-8 h-8 bg-amber-500/20 rounded-full flex items-center justify-center">
                <span className="text-amber-300 font-medium text-sm">{stats.inProgressTickets}</span>
              </div>
            </div>
            <div className="ml-3">
              <p className="text-sm font-medium text-gray-300">{t('support.admin.inProgressTickets')}</p>
            </div>
          </div>
        </div>

        <div className="bg-gray-800/30 backdrop-blur-sm rounded-lg border border-gray-700/50 p-4">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <div className="w-8 h-8 bg-green-500/20 rounded-full flex items-center justify-center">
                <span className="text-green-300 font-medium text-sm">{stats.resolvedTickets}</span>
              </div>
            </div>
            <div className="ml-3">
              <p className="text-sm font-medium text-gray-300">{t('support.admin.resolvedTickets')}</p>
            </div>
          </div>
        </div>

        <div className="bg-gray-800/30 backdrop-blur-sm rounded-lg border border-gray-700/50 p-4">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <div className="w-8 h-8 bg-red-500/20 rounded-full flex items-center justify-center">
                <span className="text-red-300 font-medium text-sm">{stats.urgentTickets}</span>
              </div>
            </div>
            <div className="ml-3">
              <p className="text-sm font-medium text-gray-300">{t('support.admin.urgentTickets')}</p>
            </div>
          </div>
        </div>

        <div className="bg-gray-800/30 backdrop-blur-sm rounded-lg border border-gray-700/50 p-4">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <div className="w-8 h-8 bg-orange-500/20 rounded-full flex items-center justify-center">
                <span className="text-orange-300 font-medium text-sm">{stats.highPriorityTickets}</span>
              </div>
            </div>
            <div className="ml-3">
              <p className="text-sm font-medium text-gray-300">{t('support.admin.highPriorityTickets')}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-gray-800/30 backdrop-blur-sm rounded-xl border border-gray-700/50 p-6 mb-6">
        <div className="flex items-center space-x-2 mb-4">
          <FunnelIcon className="w-5 h-5 text-gray-400" />
          <h2 className="text-lg font-medium text-white">Filtreler</h2>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {/* Search */}
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">
              {t('support.placeholders.searchTickets')}
            </label>
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder={t('support.placeholders.searchTickets')}
              className="w-full px-3 py-2 bg-gray-800/50 border border-gray-600 text-white placeholder-gray-400 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent backdrop-blur-sm"
            />
          </div>

          {/* Status Filter */}
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">
              {t('support.status')}
            </label>
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value as any)}
              className="w-full px-3 py-2 bg-gray-800/50 border border-gray-600 text-white rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent backdrop-blur-sm"
            >
              <option value="all">{t('common.all')}</option>
              <option value="open">{t('support.statuses.open')}</option>
              <option value="in_progress">{t('support.statuses.in_progress')}</option>
              <option value="resolved">{t('support.statuses.resolved')}</option>
            </select>
          </div>

          {/* Category Filter */}
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">
              {t('support.category')}
            </label>
            <select
              value={categoryFilter}
              onChange={(e) => setCategoryFilter(e.target.value as any)}
              className="w-full px-3 py-2 bg-gray-800/50 border border-gray-600 text-white rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent backdrop-blur-sm"
            >
              <option value="all">{t('common.all')}</option>
              <option value="general">{t('support.categories.general')}</option>
              <option value="technical">{t('support.categories.technical')}</option>
              <option value="payment">{t('support.categories.payment')}</option>
              <option value="release">{t('support.categories.release')}</option>
              <option value="account">{t('support.categories.account')}</option>
            </select>
          </div>

          {/* Priority Filter */}
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">
              {t('support.priority')}
            </label>
            <select
              value={priorityFilter}
              onChange={(e) => setPriorityFilter(e.target.value as any)}
              className="w-full px-3 py-2 bg-gray-800/50 border border-gray-600 text-white rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent backdrop-blur-sm"
            >
              <option value="all">{t('common.all')}</option>
              <option value="low">{t('support.priorities.low')}</option>
              <option value="normal">{t('support.priorities.normal')}</option>
              <option value="high">{t('support.priorities.high')}</option>
              <option value="urgent">{t('support.priorities.urgent')}</option>
            </select>
          </div>
        </div>
      </div>

      {/* Tickets List */}
      {filteredTickets.length === 0 ? (
        <div className="text-center py-12 bg-gray-800/30 backdrop-blur-sm rounded-xl border border-gray-700/50">
          <div className="w-20 h-20 bg-gray-800/50 rounded-full flex items-center justify-center mx-auto mb-4 backdrop-blur-sm">
            <ExclamationTriangleIcon className="w-10 h-10 text-gray-400" />
          </div>
          <h3 className="text-xl font-medium text-white mb-2">
            {t('support.noTickets')}
          </h3>
          <p className="text-gray-400">
            {searchQuery || statusFilter !== 'all' || categoryFilter !== 'all' || priorityFilter !== 'all'
              ? 'Filtrelere uygun bilet bulunamadı.'
              : 'Henüz destek bileti bulunmuyor.'}
          </p>
        </div>
      ) : (
        <div className="space-y-4">
          {filteredTickets.map((ticket) => (
            <div
              key={ticket.id}
              onClick={() => navigate(`/admin/support/${ticket.id}`)}
              className="bg-gray-800/30 backdrop-blur-sm rounded-xl border border-gray-700/50 hover:border-purple-500/50 transition-all duration-300 cursor-pointer p-6 hover:bg-gray-800/50 transform hover:scale-[1.02] shadow-lg"
            >
              <div className="flex items-center justify-between">
                <div className="flex-1">
                  <div className="flex items-center space-x-3 mb-3">
                    <h3 className="text-lg font-semibold text-white">
                      {ticket.subject}
                    </h3>
                    <span className={`px-3 py-1 rounded-full text-xs font-medium border ${getStatusColor(ticket.status)}`}>
                      {t(`support.statuses.${ticket.status}`)}
                    </span>
                    <span className={`px-3 py-1 rounded-full text-xs font-medium border ${getPriorityColor(ticket.priority)}`}>
                      {t(`support.priorities.${ticket.priority}`)}
                    </span>
                  </div>
                  
                  <div className="flex items-center space-x-6 text-sm text-gray-400">
                    <span className="flex items-center space-x-1">
                      <span>{t('support.submittedBy')}:</span>
                      <span className="font-medium text-gray-300">{ticket.artist?.display_name || ticket.artist?.email}</span>
                    </span>
                    <span className="flex items-center space-x-1">
                      <span>{t('support.category')}:</span>
                      <span className="font-medium text-gray-300">{t(`support.categories.${ticket.category}`)}</span>
                    </span>
                    <span className="flex items-center space-x-1">
                      <span>{t('support.created')}:</span>
                      <span className="font-medium text-gray-300">{formatDate(ticket.created_at)}</span>
                    </span>
                    <span className="flex items-center space-x-1">
                      <span>{t('support.lastUpdate')}:</span>
                      <span className="font-medium text-gray-300">{formatDate(ticket.updated_at)}</span>
                    </span>
                  </div>
                </div>
                
                <div className="flex items-center space-x-3">
                  {getStatusIcon(ticket.status)}
                  <ChevronRightIcon className="w-5 h-5 text-gray-400" />
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default AdminSupportDashboard; 